import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, TrendingUp, Users, MapPin, Send, FileEdit, Download } from "lucide-react";

const SP_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

export default function RouteAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/routes/analytics");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-slate-50"><Navbar /><main className="max-w-6xl mx-auto px-6 py-8"><p className="text-slate-500 text-sm">Loading…</p></main></div>;
  }
  if (!data) return null;

  const maxWeekly = Math.max(1, ...data.weekly.map((w) => w.stops));
  const maxSpStops = Math.max(1, ...data.salespeople.map((s) => s.stops));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Link to="/routes" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4 print:hidden">
          <ArrowLeft size={14} /> Back to routes
        </Link>
        <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Route analytics
          </h1>
          <Button variant="outline" onClick={() => window.print()} data-testid="download-pdf-btn" className="print:hidden">
            <Download size={14} className="mr-1.5" /> Download PDF
          </Button>
        </div>
        <p className="text-slate-500 mb-6">Live view of your routing operation — stops, salespeople load, and weekly trend.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={<TrendingUp size={16} />} label="Total routes" value={data.total_routes} testId="stat-total-routes" />
          <StatCard icon={<MapPin size={16} />} label="Total stops" value={data.total_stops} testId="stat-total-stops" />
          <StatCard icon={<Send size={16} />} label="Sent" value={data.sent} testId="stat-sent" tint="emerald" />
          <StatCard icon={<FileEdit size={16} />} label="Drafts" value={data.draft} testId="stat-drafts" tint="slate" />
        </div>

        <Card className="p-5 rounded-md border-slate-200 shadow-sm mb-6" data-testid="weekly-trend-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-900">Weekly stops (last 12 weeks)</h2>
            <span className="text-[11px] text-slate-500">peak: {maxWeekly}</span>
          </div>
          <div className="flex items-end gap-2 h-32">
            {data.weekly.map((w) => (
              <div key={w.week} className="flex-1 flex flex-col items-center gap-1" data-testid={`week-bar-${w.week}`}>
                <div className="w-full bg-slate-100 rounded-t relative flex items-end" style={{ height: "100%" }}>
                  <div
                    className="w-full rounded-t bg-gradient-to-t from-indigo-500 to-violet-500 transition-all"
                    style={{ height: `${(w.stops / maxWeekly) * 100}%` }}
                    title={`${w.stops} stops`}
                  />
                  {w.stops > 0 && (
                    <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] text-slate-600 font-medium">{w.stops}</span>
                  )}
                </div>
                <span className="text-[10px] text-slate-500">{w.week}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 rounded-md border-slate-200 shadow-sm" data-testid="sp-load-card">
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-900">Salesperson load</h2>
            <span className="text-[11px] text-slate-500">avg {data.avg_stops_per_route.toFixed(1)} stops/route</span>
          </div>
          {data.salespeople.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No routes yet — create one to see the load breakdown.</p>
          ) : (
            <ul className="space-y-3" data-testid="sp-list">
              {data.salespeople.map((sp, i) => {
                const pct = (sp.stops / maxSpStops) * 100;
                const color = SP_COLORS[i % SP_COLORS.length];
                return (
                  <li key={sp.email} data-testid={`sp-row-${sp.email}`}>
                    <div className="flex items-baseline justify-between text-sm">
                      <div>
                        <span className="font-semibold text-slate-900">{sp.name}</span>
                        <span className="text-slate-500 text-xs ml-2">{sp.email}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {sp.stops} stops · {sp.routes} route{sp.routes === 1 ? "" : "s"} · {sp.geocoded_stops} geocoded
                      </div>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, testId, tint = "slate" }) {
  const tints = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
  };
  return (
    <Card className="p-4 rounded-md border-slate-200 shadow-sm" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {icon} <span>{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-semibold tracking-tight ${tints[tint]}`}>{value}</div>
    </Card>
  );
}
